<script setup>

import {formatTime, timeFromTs} from "@/helpers";
import {computed, onBeforeUnmount, onMounted, reactive, ref, watch} from "vue";
import store from "@/vuex/store";
import moment from "moment";

const isLive = computed(() => store.getters["live/getIsLive"])

const stream = computed(() => store.getters["live/getStream"])
// const baseTimestamp = ref(moment().subtract(1, 'm').unix() * 1000);
const nextImageTs = ref(null);
const baseTimestamp = computed(() => {
  let diff = Date.now() - stream.value.start_timestamp - 3600000
  let time = Date.now()
  if (diff > 0) time = Math.floor((Date.now() - 3600000) / 1000) * 1000
  else time = stream.value.start_timestamp
  time -= THUMB_SECONDS * 1000
  nextImageTs.value = time
  return time
})
const THUMB_SECONDS = 6;
const PIXELS_PER_SECOND = 15;
const LIVE_UPDATE_INTERVAL = 6000;
const thumbnails = ref([]);
const thumbWidth = ref(THUMB_SECONDS * PIXELS_PER_SECOND);
const selection = ref(null)
const pendingStartIndex = ref(null);

const hoveredImage = ref(null);
const previewPos = reactive({x: 0, y: 0});

const trackRef = ref(null);
const timelineWrapper = ref(null);

const liveTimer = ref(null)

const isDragging = ref(false);
const dragType = ref(null);
const dragStartX = ref(0);
const dragStartRelX = ref(0);
const initialSelectionState = reactive({left: 0, width: 0});

const autoScroll = ref(true);


const selectionDuration = computed(() => {
  if (!selection.value) return 0;
  return (selection.value.endTimestamp - selection.value.startTimestamp) / 1000;
});

onMounted(async () => {
  const current_time = new Date().getTime();
  let diff = Math.floor((current_time - baseTimestamp.value) / 1000)
  for (let i = 0; i < Math.floor(diff / THUMB_SECONDS); i++) addThumbnail();
  liveTimer.value = setInterval(addThumbnail, LIVE_UPDATE_INTERVAL);
  window.addEventListener('keydown', (e) => {
    if (e.code === 'KeyC' && selection.value) createClip()
    if (e.code === 'Escape') {
      selection.value = null
      pendingStartIndex.value = null
    }
  })
})

onBeforeUnmount(() => {
  clearInterval(liveTimer.value);
  window.removeEventListener('mousemove', onDrag);
  window.removeEventListener('mouseup', stopDrag);
  window.removeEventListener('keydown', (e) => {
    if (e.code === 'KeyC' && selection.value) createClip()
    if (e.code === 'Escape') selection.value = null
  })
})


function timestampFromX(x,) {
  return baseTimestamp.value + (x / PIXELS_PER_SECOND) * 1000;
}

const snapToSecond = (pixels) => {
  return Math.floor(pixels / PIXELS_PER_SECOND) * PIXELS_PER_SECOND;
};

function addThumbnail() {
  const id = thumbnails.value.length + 1;
  // const ts = baseTimestamp.value + ((id - 1) * THUMB_SECONDS * 1000);
  nextImageTs.value += THUMB_SECONDS * 1000
  const seed = Math.random().toString(36).substring(7);

  // const url = `http://gpu8.hibridmedia.com:8081/hibrid/playout_240p/dvr_thumbnail_${Math.floor(ts / 1000)}.jpg`
  const url = `/api/shared/proxy-image?id=${store.getters["live/getSeatId"]}&time=${Math.floor(nextImageTs.value / 1000)}`
  // const url = `https://picsum.photos/seed/${seed}/300/180`;
  thumbnails.value.push({
    id,
    url: url,
    timestamp: nextImageTs.value,
    image_retry: 0
  });
  if (autoScroll.value && timelineWrapper.value) {
    setTimeout(() => {
      while(thumbnails.value.length >= (3600 / THUMB_SECONDS)) thumbnails.value.shift();
      timelineWrapper.value.scrollLeft = timelineWrapper.value.scrollWidth;
    }, 0);
  }
}

// --- SELECTION LOGIC ---
const getRelativeMouseX = (event) => {
  if (!trackRef.value) return 0;
  const rect = trackRef.value.getBoundingClientRect();
  return event.clientX - rect.left;
};

const startDragSelection = (event) => {
  // Record initial Client X to distinguish click from drag later
  dragStartX.value = event.clientX;
  const relX = getRelativeMouseX(event);
  const timelineWidth = thumbnails.value.length * thumbWidth.value;

  if (relX < 0 || relX > timelineWidth) return;

  if (selection.value && event.target.closest('.selection-box')) {
    isDragging.value = true;
    dragType.value = 'move';
    dragStartRelX.value = relX - selection.value.left;
    initialSelectionState.left = selection.value.left;
    initialSelectionState.width = selection.value.width;
  } else {
    isDragging.value = true;
    dragType.value = 'create';
    dragStartRelX.value = relX;

    selection.value = {
      left: relX,
      width: 0,
      startTimestamp: timestampFromX(relX),
      endTimestamp: timestampFromX(relX)
    };
  }

  window.addEventListener('mousemove', onDrag);
  window.addEventListener('mouseup', stopDrag);
};

const startDragHandle = (event, side) => {
  dragStartX.value = event.clientX; // Track start for handle too
  isDragging.value = true;
  dragType.value = side;
  initialSelectionState.left = selection.value.left;
  initialSelectionState.width = selection.value.width;
  dragStartRelX.value = event.clientX;

  window.addEventListener('mousemove', onDrag);
  window.addEventListener('mouseup', stopDrag);
};

const onDrag = (event) => {
  autoScroll.value = false
  if (!isDragging.value) return;

  const timelineWidth = thumbnails.value.length * thumbWidth.value;

  if (dragType.value === 'create') {
    const currentRelX = getRelativeMouseX(event);
    let startX = dragStartRelX.value;
    let currentX = Math.max(0, Math.min(currentRelX, timelineWidth));
    currentX = snapToSecond(currentX);
    startX = snapToSecond(startX)

    let newLeft, newWidth;
    if (currentX > startX) {
      newLeft = startX;
      newWidth = currentX - startX;
    } else {
      newLeft = currentX;
      newWidth = startX - currentX;
    }

    selection.value = {
      left: newLeft,
      width: newWidth,
      startTimestamp: timestampFromX(newLeft),
      endTimestamp: timestampFromX(newLeft + newWidth)
    };

  } else if (dragType.value === 'move') {
    const currentRelX = getRelativeMouseX(event);
    let newLeft = currentRelX - dragStartRelX.value;
    newLeft = snapToSecond(newLeft);

    if (newLeft < 0) newLeft = 0;
    if (newLeft + initialSelectionState.width > timelineWidth) {
      newLeft = timelineWidth - initialSelectionState.width;
    }

    selection.value = {
      left: newLeft,
      width: initialSelectionState.width,
      startTimestamp: timestampFromX(newLeft),
      endTimestamp: timestampFromX(newLeft + initialSelectionState.width)
    };

  } else if (dragType.value === 'left') {
    const deltaX = event.clientX - dragStartRelX.value;
    let newLeft = initialSelectionState.left + deltaX;
    newLeft = Math.max(0, newLeft);
    newLeft = snapToSecond(newLeft);
    const maxLeft = initialSelectionState.left + initialSelectionState.width;
    newLeft = Math.min(newLeft, maxLeft);

    selection.value = {
      left: newLeft,
      width: (initialSelectionState.left + initialSelectionState.width) - newLeft,
      startTimestamp: timestampFromX(newLeft),
      endTimestamp: selection.value.endTimestamp
    };

  } else if (dragType.value === 'right') {
    const deltaX = event.clientX - dragStartRelX.value;
    let newWidth = initialSelectionState.width + deltaX;
    const maxWidth = timelineWidth - initialSelectionState.left;

    newWidth = Math.max(0, Math.min(newWidth, maxWidth));
    newWidth = snapToSecond(newWidth);

    selection.value = {
      left: selection.value.left,
      width: newWidth,
      startTimestamp: selection.value.startTimestamp,
      endTimestamp: timestampFromX(selection.value.left + newWidth)
    };
  }
};

const stopDrag = (event) => {
  isDragging.value = false;
  const type = dragType.value;
  dragType.value = null;

  window.removeEventListener('mousemove', onDrag);
  window.removeEventListener('mouseup', stopDrag);

  // Check if this was a "Click" (movement < 5px) vs a "Drag"
  const totalDelta = Math.abs(event.clientX - dragStartX.value);

  if (totalDelta < 5) {
    // It was a click!
    const relX = getRelativeMouseX(event);
    const clickedIndex = Math.floor(relX / thumbWidth.value);
    handleThumbnailClick(clickedIndex);
  } else {
    // It was a drag.
    // If it was a drag, we clear any "pending" click state to avoid confusion
    pendingStartIndex.value = null;
  }

  if (selection.value && selection.value.width < 2) {
    selection.value = null;
  }
};

// NEW: Click-to-Select Logic
const handleThumbnailClick = (index) => {
  autoScroll.value = false;
  if (pendingStartIndex.value === null) {
    // First click: Set as pending
    pendingStartIndex.value = index;
    // Optional: Clear existing selection if starting a new click chain
    selection.value = null;
  } else {
    // Second click: Create Range
    const start = Math.min(pendingStartIndex.value, index);
    const end = Math.max(pendingStartIndex.value, index);

    // Create selection covering these thumbnails
    const newLeft = start * thumbWidth.value;
    const newWidth = ((end - start) + 1) * thumbWidth.value;

    selection.value = {
      left: newLeft,
      width: newWidth,
      startTimestamp: timestampFromX(newLeft),
      endTimestamp: timestampFromX(newLeft + newWidth)
    };

    pendingStartIndex.value = null; // Reset
  }
};

function createClip() {
  if (!selection.value) return;
  const thumbIndex = Math.floor(selection.value.left / thumbWidth.value);
  const safeIndex = Math.min(thumbIndex, thumbnails.value.length - 1);
  const thumbnailUrl = thumbnails.value[safeIndex]?.url || '';

  const clip_data = {
    id: Date.now(),
    startTimestamp: selection.value.startTimestamp,
    endTimestamp: selection.value.endTimestamp,
    duration: selectionDuration.value,
    thumbnail: thumbnailUrl,
    transition: null,
    loading: true,
    video_url: null
  }
  store.dispatch('live/createClip', clip_data);
  selection.value = null
}

const showPreview = (thumb) => {
  hoveredImage.value = thumb;
};

const movePreview = (event) => {
  if (!hoveredImage.value) return;
  // Position under mouse with offset
  previewPos.x = event.clientX + 20;
  previewPos.y = event.clientY + 20;
};

const hidePreview = () => {
  hoveredImage.value = null;
};

</script>

<template>
  <section class="mx-auto">
    <!--    <div class="px-8 py-2 rounded-[4px] bg-white border border-[!F3F4F5]">-->
    <div class="rounded-[4px] bg-white mt-2">

      <!-- Timeline Header Info -->
      <div class="flex justify-between items-center mb-5">
        <div class="flex items-start gap-2">
          <h2 class="text-[18px] font-bold text-dark_blue">Live Feed</h2>
          <!--          <strong class="text-[10px] text-blue-450">{{timeFromTs(stream.start_timestamp)}}</strong>-->
        </div>

        <button
          @click="createClip"
          class="flex gap-4 items-center relative px-10 py-3 text-xs font-bold text-white rounded-[4px] transition"
          :class="{
            'cursor-pointer bg-blue-450 hover:bg-blue-450/80': selection,
            'cursor-not-allowed bg-gray-400': !selection
          }"
        >
          <i class="fa fa-cut"></i>
          Clip
        </button>

        <div class="flex items-center gap-4 min-w-[320px] justify-end">
          <div class="flex items-center gap-3">
            <span class="text-xs font-semibold text-slate-500 dark:text-slate-400">Live</span>
            <div class="relative inline-block w-10 mr-2 align-middle select-none transition duration-200 ease-in">
              <el-switch v-model="autoScroll" style="--el-switch-on-color: #00b4ff;"/>
            </div>
          </div>
          <div class="flex items-center gap-3 bg-blue-450 px-4 py-1.5 rounded-full ">
              <span class="text-white font-mono text-[12px]">{{
                  selection ? formatTime(selection.startTimestamp) : '--:--:--'
                }}</span>
            <div class="w-px h-3 bg-white"></div>
            <span class="text-white font-mono text-[12px]">{{
                selection ? formatTime(selection.endTimestamp) : '--:--:--'
              }}</span>
          </div>
          <div v-if="selection"
               class="text-xs font-mono bg-indigo-500/10 text-indigo-300 px-2 py-1 rounded">
            {{ selectionDuration.toFixed(1) }}s
          </div>
        </div>
      </div>

      <!-- Timeline Component -->
      <div class="timeline-wrapper select-none" ref="timelineWrapper" @mousemove="movePreview">
        <!-- Ruler -->
        <div
          class="time-ruler"
          :style="{ width: (thumbnails.length * thumbWidth) + 'px' }"
        >
          <div
            v-for="thumb in thumbnails"
            :key="'tick-'+thumb.id"
            class="time-tick"
            :style="{ width: thumbWidth + 'px' }"
          >
            {{ formatTime(thumb.timestamp) }}
          </div>
        </div>

        <!-- Track -->
        <div
          ref="trackRef"
          class="timeline-track"
          @mousedown="startDragSelection"
          :style="{ width: (thumbnails.length * thumbWidth) + 'px' }"
        >
          <div class="thumbnail-strip">
            <!-- Moved @mouseenter to wrapper because wrapper now has pointer-events: auto -->
            <div
              v-for="(thumb, index) in thumbnails"
              :key="thumb.id"
              class="thumb-wrapper"
              :class="{ 'is-pending': pendingStartIndex === index }"
              :style="{ width: thumbWidth + 'px' }"
              @mouseenter="showPreview(thumb)"
              @mouseleave="hidePreview"
            >
              <el-image :src="thumb.url" lazy class="thumbnail-item" fit="cover" :draggable="false">
                <template #placeholder>
                  <img class="thumbnail-item" src="/images/loading.jpg" alt="loading"/>
                </template>
                <template #error>
                  <img class="thumbnail-item" src="/images/missing.jpg" alt="missing"/>
                </template>
              </el-image>
            </div>
          </div>

          <div
            v-if="selection"
            class="selection-box"
            :style="{ left: selection.left + 'px', width: selection.width + 'px' }"
          >
            <div
              class="resize-handle handle-left"
              @mousedown.stop="startDragHandle($event, 'left')"
            >
              <i class="fas fa-chevron-left"></i>
            </div>
            <div
              class="resize-handle handle-right"
              @mousedown.stop="startDragHandle($event, 'right')"
            >
              <i class="fas fa-chevron-right"></i>
            </div>
          </div>

        </div>
      </div>

      <!--      <div class=" flex gap-2 mt-8 justify-center">-->
      <!--        -->
      <!--      </div>-->
    </div>
  </section>

  <transition name="tooltip">
    <div v-if="hoveredImage" class="preview-tooltip"
         :style="{ top: previewPos.y + 20 + 'px', left: (previewPos.x - 180) + 'px' }">
      <img :src="hoveredImage.url" class="block w-[320px] h-[180px] " draggable="false" alt="Preview">
      <div
        class="absolute bottom-2 left-3 text-xs font-mono font-bold text-white drop-shadow-md px-2 py-1 bg-black/70 rounded-[4px]">
        {{ formatTime(hoveredImage.timestamp) }}
      </div>
    </div>
  </transition>
</template>

